/**
 * CreateIssueModal (ADR-0110) — the "+ New issue" form on the Engineering page.
 *
 * The inflow capture: an engineering task is filed here as a GitHub issue on
 * the lf2.0 tracker. Deliberately minimal — a required title and an optional body.
 * The route auto-stamps the `lf2.0` label, so the form carries no label/assignee
 * picker (categorisation is a later pass; manual pin is the only float in v1).
 *
 * On success: invalidates ["engineering"] (the useCreateIssue hook does this),
 * closes the modal, resets state.
 */

import { useState } from "react";
import type { CreateIssueRequest } from "@command-center/shared";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateIssue } from "@/lib/use-engineering";

export function CreateIssueModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const createIssue = useCreateIssue();

  const reset = () => {
    setTitle("");
    setBody("");
    createIssue.reset();
  };

  const close = () => {
    if (createIssue.isPending) return;
    reset();
    onClose();
  };

  const canSubmit = title.trim() !== "" && !createIssue.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const req: CreateIssueRequest = {
      title: title.trim(),
      ...(body.trim() !== "" ? { body: body.trim() } : {}),
    };
    try {
      await createIssue.mutateAsync(req);
      reset();
      onClose();
    } catch {
      // Error surfaces via createIssue.isError below; keep the modal open.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      labelledBy="create-issue-title"
      describedBy="create-issue-description"
      testId="create-issue-modal"
    >
      <DialogHeader>
        <DialogTitle id="create-issue-title">+ New issue</DialogTitle>
        <DialogDescription id="create-issue-description">
          File an engineering issue on the lf2.0 tracker. It lands here instead
          of the operator Dashboard.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="space-y-1">
          <label
            htmlFor="create-issue-title-input"
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            Title
          </label>
          <Input
            id="create-issue-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What's the bug / gap / task?"
            data-testid="create-issue-title-input"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="create-issue-body-input"
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            Details
            <span className="ml-1 normal-case text-muted-foreground/70">
              (optional)
            </span>
          </label>
          <Textarea
            id="create-issue-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Repro, context, links — anything the fix will need."
            rows={5}
            data-testid="create-issue-body-input"
          />
        </div>

        {createIssue.isError ? (
          <div
            className="rounded border border-error-border bg-error-bg p-2 text-xs text-error"
            data-testid="create-issue-error"
          >
            Couldn't create the issue. {String(createIssue.error)}
          </div>
        ) : null}
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={close}
          disabled={createIssue.isPending}
          data-testid="create-issue-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="create-issue-submit"
        >
          {createIssue.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
